import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { ContactForm } from "@/components/site/ContactForm";
import { CaseCard } from "@/components/site/case/CaseCard";
import { getCaseBySlug, getRelatedCases, type CaseDetail } from "@/content/cases";
import { breadcrumbs, caseStudy, aeoGeo } from "@/lib/seo/schema";
import { abs, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";

const SITE_ORIGIN = "https://fenny.se";

export const Route = createFileRoute("/case_/$slug")({
  loader: ({ params }): { item: CaseDetail; related: CaseDetail[] } => {
    const item = getCaseBySlug(params.slug);
    if (!item) throw notFound();
    const related = getRelatedCases(params.slug, item.industry);
    return { item, related };
  },
  head: ({ params, loaderData }) => {
    const item = loaderData?.item;
    const title = item ? `${item.title} — ${item.client} | Case` : "Case | Fenny";
    const description = item?.seoDescription ?? item?.excerpt ?? "Case från Fenny.";
    // Använd alltid huvud-OG-bilden så delningar i LinkedIn/Slack/X/FB
    // visar samma featured image — YouTube-thumbnail används endast i schema.
    const og = SOCIAL_IMAGE_URL;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://fenny.se/case/${params.slug}` },
        { property: "og:image", content: og },
        { name: "twitter:image", content: og },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: `https://fenny.se/case/${params.slug}` },
        { rel: "preconnect", href: "https://i.ytimg.com" },
        { rel: "preconnect", href: "https://www.youtube.com" },
      ],

      scripts: item
        ? [
            { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: `/case/${params.slug}`, name: title, description })) },
            // CaseStudy — rikt schema med klient, bransch, mätbara resultat
            // och inbäddad film. Driver E-E-A-T + citerbarhet i AI Overviews.
            {
              type: "application/ld+json",
              children: JSON.stringify(
                caseStudy({
                  name: item.title,
                  description: item.excerpt ?? "",
                  path: `/case/${params.slug}`,
                  image: og,
                  clientName: item.client,
                  industry: item.industry,
                  ...(item.year && { datePublished: `${item.year}-01-01` }),
                  about: [item.category, item.industry].filter(Boolean) as string[],
                  ...(item.metrics && item.metrics.length > 0 && {
                    results: item.metrics.map(
                      (m: { value: string; label: string }) => `${m.value} ${m.label}`,
                    ),
                  }),
                  video: {
                    name: `${item.title} — ${item.client}`,
                    description: item.excerpt ?? item.title,
                    thumbnailUrl: og ?? abs("/og/og-default.jpg"),
                    uploadDate: item.year ? `${item.year}-01-01` : "2024-01-01",
                    embedUrl: `https://www.youtube-nocookie.com/embed/${item.id}`,
                    contentUrl: `https://www.youtube.com/watch?v=${item.id}`,
                  },
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbs([
                  { name: "Hem", path: "/" },
                  { name: "Case", path: "/case" },
                  { name: item.title, path: `/case/${params.slug}` },
                ]),
              ),
            },
          ]
        : [],

    };
  },
  component: CaseDetailPage,
  notFoundComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Caset finns inte</h1>
      <p className="mt-3 text-muted-foreground">Det här caset kan ha tagits bort eller flyttats.</p>
      <div className="mt-6">
        <Button to="/case" variant="mint" arrow>Till alla case</Button>
      </div>
    </div>
  ),
  errorComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Något gick fel</h1>
      <p className="mt-3 text-muted-foreground">Caset kunde inte laddas. Försök igen.</p>
    </div>
  ),
});


function CaseDetailPage() {
  const { item, related } = Route.useLoaderData();

  return (
    <article className="pt-28">
      <Section className="pb-6">
        <Link to="/case" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-mint">
          <ArrowLeft className="h-4 w-4" /> Alla case
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-mint">
          <span>{item.service}</span>
          <span aria-hidden className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{item.industry}</span>
          {item.year && (<><span aria-hidden className="text-muted-foreground">·</span><span className="text-muted-foreground">{item.year}</span></>)}
        </div>
        <h1 className="mt-5 text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
          {item.title}
        </h1>
        <p className="mt-5 max-w-3xl text-pretty text-lg text-muted-foreground md:text-xl">
          {item.excerpt}
        </p>
      </Section>

      <Section className="py-6">
        <VideoEmbed id={item.id} title={item.title} priority="high" />
      </Section>

      {item.metrics && item.metrics.length > 0 && (
        <Section className="py-8">
          <div className="grid grid-cols-2 gap-6 border-y border-white/10 py-8 md:grid-cols-3 md:gap-10 md:py-10">
            {item.metrics.map((m: { value: string; label: string }) => (
              <div key={m.label}>
                <div className="text-4xl font-semibold leading-none text-butter md:text-5xl">{m.value}</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">{m.label}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section className="py-10">
        <div className="grid gap-10 md:grid-cols-3">
          <Block title="Utmaningen" body={item.challenge} accent="peach" />
          <Block title="Lösningen" body={item.solution} accent="mint" />
          <Block title="Resultatet" body={item.outcome} accent="butter" />
        </div>

        {item.scope && item.scope.length > 0 && (
          <div className="mt-12 border-t border-white/10 pt-8">
            <div className="mb-4 text-xs uppercase tracking-widest text-muted-foreground">Vi gjorde</div>
            <div className="flex flex-wrap gap-2">
              {item.scope.map((s: string) => (
                <span key={s} className="rounded-full border border-white/15 bg-surface px-3 py-1.5 text-sm">{s}</span>
              ))}
            </div>
          </div>
        )}
      </Section>

      {item.quote && (
        <Section className="bg-surface/40">
          <blockquote className="mx-auto max-w-3xl text-balance text-center text-2xl font-medium leading-snug md:text-3xl">
            <span className="text-mint">"</span>{item.quote.text}<span className="text-mint">"</span>
            <footer className="mt-6 text-sm uppercase tracking-widest text-muted-foreground">
              — {item.quote.name}, {item.quote.role}
            </footer>
          </blockquote>
        </Section>
      )}

      {item.gallery && item.gallery.length > 0 && (
        <Section className="py-8">
          <div className="mb-6 text-xs uppercase tracking-widest text-mint">Mer från samma värld</div>
          <div className="grid gap-5 md:grid-cols-2">
            {item.gallery.map((id: string) => (
              <VideoEmbed key={id} id={id} title={`${item.client} — relaterad film`} />
            ))}
          </div>
        </Section>
      )}

      <Section>
        <div className="rounded-3xl border border-white/10 bg-surface/60 p-8 text-center md:p-12">
          <h2 className="text-balance text-3xl font-semibold md:text-4xl">Vill ni ha samma effekt?</h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Berätta vad ni vill uppnå — vi återkommer med vad det skulle kosta och hur snabbt vi kan vara live.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button to="/kontakt" variant="mint" arrow trackId="book_strategy_meeting" trackProps={{ location: "case_detail", slug: item.slug }}>
              Boka strategimöte
            </Button>
            <Button to={item.service === "Pulse" ? "/pulse" : "/prime"} variant="outline">
              Så funkar {item.service}
            </Button>
          </div>
        </div>
      </Section>

      {related.length > 0 && (
        <Section>
          <div className="mb-6 text-xs uppercase tracking-widest text-mint">Liknande case</div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {related.map((c: CaseDetail) => (
              <CaseCard key={c.id} item={c} />
            ))}
          </div>
        </Section>
      )}

      <ContactForm location={`case_detail_${item.slug}`} />
    </article>
  );
}

function Block({ title, body, accent }: { title: string; body?: string; accent: "mint" | "peach" | "butter" }) {
  if (!body) return null;
  const accentClass = accent === "mint" ? "text-mint" : accent === "peach" ? "text-peach" : "text-butter";
  return (
    <div>
      <div className={`mb-3 text-xs uppercase tracking-widest ${accentClass}`}>{title}</div>
      <p className="text-pretty text-base leading-relaxed text-foreground/85 md:text-lg">{body}</p>
    </div>
  );
}
